import {
  FlexBox,
  space,
  Table,
  Tag,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { i18n as I18n, TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { ReactComponent as TimeIcon } from "../../public/material-icons/access-time.svg";
import { ReactComponent as ArrowIcon } from "../../public/material-icons/arrow-forward.svg";
import { ReactComponent as ErrorIcon } from "../../public/material-icons/error-outline.svg";
import { ReactComponent as LabelIcon } from "../../public/material-icons/label-outline.svg";
import { ReactComponent as IncandescentIcon } from "../../public/material-icons/wb-incandescent.svg";
import type { IncidentHistory } from "../../server/lads-api/clients/lcc-openapi/models/IncidentHistory";
import { receiveLADSIncidentHistory } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import {
  findIncidentChannel,
  formatIncidentMessage,
} from "../util/incident-message";
import {
  formatDateWithSuffix,
  getTimeDifference,
} from "../util/time-formatter";
import type { Channel, Device, Incident } from "../view-model";

type ErrorReportOneProps = {
  incident: Incident;
  device?: Device;
  /** Channels für die Label-Auflösung (Kette ComponentName → displayName →
   * browseName); Fallback ist device.channels. */
  channels?: Channel[];
  comment: string;
  setComment: (value: string) => void;
};
IncandescentIcon;

// i18n-Auflösung: erst voller Name, dann FU-relativer Kurzname, dann der
// formatierte Grenzwert-Text, sonst die rohe Message bzw. ein Fallback.
const resolveIncidentText = (
  incident: Incident,
  headerIncident: Incident,
  t: TFunction,
  i18n: I18n,
  channelLabel?: string
): string => {
  if (i18n.exists(`${incident.name}.message`, { ns: "incidents" })) {
    return t(`${incident.name}.message`, { ns: "incidents" });
  }
  const shortName = incident.name.split(".").slice(1).join(".");
  if (i18n.exists(`${shortName}.message`, { ns: "incidents" })) {
    return t(`${shortName}.message`, { ns: "incidents" });
  }
  if (headerIncident.limitState) {
    return formatIncidentMessage(headerIncident, t, channelLabel);
  }
  return incident.message || t("unknown.message");
};

const resolveIncidentHelp = (
  incident: Incident,
  t: TFunction,
  i18n: I18n
): string | undefined => {
  if (i18n.exists(`${incident.name}.help`, { ns: "incidents" })) {
    return t(`${incident.name}.help`, { ns: "incidents" }) ?? undefined;
  }
  const shortName = incident.name.split(".").slice(1).join(".");
  if (i18n.exists(`${shortName}.help`, { ns: "incidents" })) {
    return t(`${shortName}.help`, { ns: "incidents" }) ?? undefined;
  }
  return incident.help;
};

const StyledTextarea = styledComponents.textarea<{ $invalid?: boolean }>`
  width: 100%;
  min-height: 80px;
  padding: ${space(2)};
  font-family: inherit;
  font-size: ${theme.font.size.default.sm};
  border: 1px solid ${(p) =>
    p.$invalid ? theme.color.error : theme.colorPalette.grey300};
  border-radius: 4px;
  resize: vertical;
  background: ${(p) =>
    p.$invalid ? "rgba(220, 38, 38, 0.04)" : "transparent"};
  &:focus {
    outline: none;
    border-color: ${(p) =>
      p.$invalid ? theme.color.error : theme.color.primary};
  }
`;

const StyledTimeline = styledComponents.div`
  margin-top: ${space(2)};
  padding: ${space(3)} ${space(4)};
  border: 1px solid ${theme.colorPalette.grey200};
  border-radius: 8px;
  background: ${theme.colorPalette.grey100};
  max-height: 280px;
  overflow-y: auto;
`;

const StyledTimelineInner = styledComponents.div`
  position: relative;
  padding-left: ${space(4)};
  &::before {
    content: "";
    position: absolute;
    left: 4px;
    top: ${space(2)};
    bottom: ${space(2)};
    width: 2px;
    background: ${theme.colorPalette.grey300};
  }
`;

const StyledTimelineEntry = styledComponents.div<{ dotColor: string }>`
  position: relative;
  padding: ${space(2)} 0;
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colorPalette.grey200};
  }
  &::before {
    content: "";
    position: absolute;
    left: -${space(4)};
    top: ${space(3)};
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) => p.dotColor};
    box-shadow: 0 0 0 3px ${theme.colorPalette.grey100};
  }
`;

// Verlauf zeigt jedes Event einzeln — Operator soll Warning/Critical/
// Resolve-Flicker als individuelle Zeilen sehen, nicht zusammengefasst.

/**
 * Parse the limit-state tag out of an alarm-state event's message
 * ("Condition is X and state is HighHigh"). Drives severity-aware coloring
 * for active entries: HighHigh / LowLow → critical (red), High / Low →
 * warning (yellow). Returns undefined for non-alarm-state entries or when
 * the message format isn't recognised.
 */
function entrySeverityTag(
  entry: IncidentHistory
): "critical" | "warning" | undefined {
  if (entry.status !== "NEW") return undefined;
  const m = /\bstate is (highhigh|high|lowlow|low)\b/i.exec(
    entry.message ?? ""
  );
  if (!m) return undefined;
  const tag = m[1]!.toLowerCase();
  return tag === "highhigh" || tag === "lowlow" ? "critical" : "warning";
}

// Severity-Label aus der rohen Event-Message: HighHigh/LowLow → Error,
// High/Low → Warning (über i18n incident.type.*). Operator soll Warning/Error
// sehen, nicht das rohe OPC-UA-Token "HighHigh".
function limitLabelOf(
  entry: IncidentHistory,
  t: (key: string, opts?: { defaultValue?: string }) => string
): string | null {
  const m = /\bstate is (highhigh|high|lowlow|low)\b/i.exec(
    entry.message ?? ""
  );
  if (!m) return null;
  const tag = m[1]!.toLowerCase();
  return tag === "highhigh" || tag === "lowlow"
    ? t("incident.type.critical", { defaultValue: "Error" })
    : t("incident.type.warning", { defaultValue: "Warning" });
}

// Verlauf-Label: eine Eskalation wird mit ihrer Limit-Stufe gezeigt statt mit
// generischem "Aktiv/NEW" — "NEW" brauchen wir im Verlauf nicht (der
// Episodenstart steht oben als "Beginn"). Audit-/Resolved-Stati behalten ihr
// Übersetzungs-Label.
function statusLabelOf(
  entry: IncidentHistory,
  t: (key: string, opts?: { defaultValue?: string }) => string
): string {
  if (entry.status === "NEW") {
    return (
      limitLabelOf(entry, t) ??
      t("incident.status.new", { defaultValue: "Active" })
    );
  }
  return t(`incident.status.${entry.status.toLowerCase()}`, {
    defaultValue: entry.status,
  });
}

function tagVariantFor(
  entry: IncidentHistory
): "info" | "warning" | "error" | "neutral" {
  switch (entry.status) {
    case "NEW": {
      return entrySeverityTag(entry) === "warning" ? "warning" : "error";
    }
    case "REPORTED": {
      return "info";
    } // Gemeldet → blau
    case "ACKNOWLEDGED": {
      return "info";
    } // Quittiert → blau
    case "UPDATED": {
      return "info";
    } // Updated → blau
    case "RESOLVED":
    case "CLOSED":
    case "CONFIRMED": {
      return "info";
    } // Behoben/Bestätigt → Tag info (blau)
    default: {
      return "neutral";
    }
  }
}

function dotColorFor(entry: IncidentHistory): string {
  switch (entry.status) {
    case "NEW": {
      return entrySeverityTag(entry) === "warning"
        ? theme.color.warning
        : theme.color.error;
    }
    case "REPORTED": {
      return theme.color.info;
    } // Gemeldet → blau
    case "ACKNOWLEDGED": {
      return theme.color.info;
    } // Quittiert → blau
    case "RESOLVED":
    case "CLOSED": {
      return theme.color.info;
    } // Behoben → blau (Tag info), Bullet == Schriftfarbe
    case "CONFIRMED": {
      return theme.color.info;
    } // Bestätigt → blau (Tag info)
    case "UPDATED": {
      return theme.color.info;
    } // Updated → blau
    default: {
      return theme.colorPalette.grey400;
    }
  }
}

// Wie lange eine Warning/Error-Eskalation in diesem Zustand blieb: vom
// Eskalations-Event bis zur nächsten Zustandsänderung (Resolve/Close/Confirm
// oder die nächste Eskalation). Audit-Events (Gemeldet/Quittiert/Updated)
// beenden den aktiven Alarm NICHT und zählen daher nicht als Ende. Noch
// laufend (kein End-Event) → bis jetzt. `entries` ist chronologisch (ältestes
// zuerst), daher schauen wir vorwärts.
const ESCALATION_END_STATES = new Set([
  "RESOLVED",
  "CLOSED",
  "CONFIRMED",
  "NEW",
]);
function escalationDuration(entries: IncidentHistory[], idx: number): string {
  const start = new Date(entries[idx]!.updatedAt);
  let end = new Date();
  for (let j = idx + 1; j < entries.length; j++) {
    if (ESCALATION_END_STATES.has(entries[j]!.status)) {
      end = new Date(entries[j]!.updatedAt);
      break;
    }
  }
  return getTimeDifference(start, end);
}

// "Acknowledged" stays in the suppression list because Ack is the only audit
// action that may legitimately ship without an operator comment — backend
// then writes the default string and the status tag already conveys it.
// Confirm and Report require a comment by contract, so their messages are
// always real operator text and must be displayed.
const REDUNDANT_AUDIT_MESSAGES = new Set(["acknowledged"]);

export const ErrorReportOne = ({
  channels,
  comment,
  device,
  incident,
  setComment,
}: ErrorReportOneProps) => {
  const { i18n, t } = useTranslation();
  const accessToken = useAccessToken();
  const [history, setHistory] = useState<IncidentHistory[]>([]);

  useEffect(() => {
    let cancelled = false;
    receiveLADSIncidentHistory(incident.id, accessToken ?? undefined)
      .then((res) => {
        if (cancelled) return;
        setHistory(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
    // Bei JEDER Zustandsänderung des Incidents neu laden — sonst bleibt der
    // Verlauf stehen, während Quittieren/Bestätigen/Melden/Auflösen
  }, [
    incident.id,
    accessToken,
    incident.active,
    incident.status,
    incident.acknowledged,
    incident.confirmed,
    incident.reported,
    incident.closedAt,
    incident.severity,
    incident.limitState,
  ]);

  const headerSeverity = incident.maxSeverity ?? incident.severity;
  const headerIncident = incident;

  return (
    <>
      <Typography size="xl" weight="semiBold">
        {`${t("header.errorcode")} #${incident?.code}`}
      </Typography>
      <Table>
        <Table.Head>
          <Table.Row padding={`${space(4)} ${space(4)} ${space(4)} 0`}>
            <Table.Header />
            <Table.Header columnWidth="400px" />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          <Table.Row padding={`${space(4)} ${space(4)} ${space(4)} 0`}>
            <Table.Data>
              <FlexBox alignItems="center" gap={space(1)}>
                <LabelIcon
                  color={theme.colorPalette.grey400}
                  fontSize={theme.font.size.default.base}
                />

                <Typography color="black" size="xs" weight="semiBold">
                  {t("header.severity")}
                </Typography>
              </FlexBox>
            </Table.Data>
            <Table.Data>
              {/* Kopfzeile zeigt den schlimmsten Grad der Episode (Error >
                  Warning), aus dem Verlauf — nicht den aktuell letzten Stand. */}
              <Tag
                variant={headerSeverity === "critical" ? "error" : "warning"}
              >
                {t(`incident.type.${headerSeverity}`)}
              </Tag>
            </Table.Data>
          </Table.Row>
          <Table.Row padding={`${space(4)} ${space(4)} ${space(4)} 0`}>
            <Table.Data>
              <FlexBox alignItems="center" gap={space(1)}>
                <ErrorIcon
                  color={theme.colorPalette.grey400}
                  fontSize={theme.font.size.default.base}
                />
                <Typography color="black" size="xs" weight="semiBold">
                  {t("report.errormessage")}
                </Typography>
              </FlexBox>
            </Table.Data>
            <Table.Data>
              <Typography size="sm">
                {resolveIncidentText(
                  incident,
                  headerIncident,
                  t,
                  i18n,
                  findIncidentChannel(
                    incident,
                    channels ?? Object.values(device?.channels ?? {})
                  )?.name
                )}
              </Typography>
            </Table.Data>
          </Table.Row>
          {(i18n.exists(`${incident.name}.help`, { ns: "incidents" }) ||
            i18n.exists(`${incident.name.split(".").slice(1).join(".")}.help`, {
              ns: "incidents",
            }) ||
            incident.help) && (
            <Table.Row padding={`${space(4)} ${space(4)} ${space(4)} 0`}>
              <Table.Data>
                <FlexBox alignItems="center" gap={space(1)}>
                  <IncandescentIcon
                    color={theme.colorPalette.grey400}
                    fontSize={theme.font.size.default.base}
                  />
                  <Typography color="black" size="xs" weight="semiBold">
                    {t("report.reference")}
                  </Typography>
                </FlexBox>
              </Table.Data>
              <Table.Data>
                <Typography size="sm">
                  {resolveIncidentHelp(incident, t, i18n)}
                </Typography>
              </Table.Data>
            </Table.Row>
          )}
          <Table.Row padding={`${space(4)} ${space(4)} ${space(7)} 0`}>
            <Table.Data>
              <FlexBox alignItems="center" gap={space(1)}>
                <TimeIcon
                  color={theme.colorPalette.grey400}
                  fontSize={theme.font.size.default.base}
                />
                <Typography color="black" size="xs" weight="semiBold">
                  {t("report.time")}
                </Typography>
              </FlexBox>
            </Table.Data>
            <Table.Data>
              <FlexBox
                gap={space(3)}
                justifyContent="space-between"
                width="fit-content"
              >
                <FlexBox flexDirection="column">
                  <Typography size="sm">{t("report.start")}:</Typography>
                  {incident.closedAt && (
                    <Typography size="sm">{t("report.end")}:</Typography>
                  )}
                  <Typography size="sm">{t("report.duration")}:</Typography>
                </FlexBox>
                <FlexBox flexDirection="column">
                  <Typography size="sm">
                    {formatDateWithSuffix(new Date(incident.createdAt))}
                  </Typography>
                  {incident.closedAt && (
                    <Typography size="sm">
                      {formatDateWithSuffix(new Date(incident.closedAt))}
                    </Typography>
                  )}
                  <Typography size="sm">
                    {incident.closedAt
                      ? getTimeDifference(
                          new Date(incident.createdAt),
                          new Date(incident.closedAt)
                        )
                      : getTimeDifference(
                          new Date(incident.createdAt),
                          new Date()
                        )}
                  </Typography>
                </FlexBox>
              </FlexBox>
            </Table.Data>
          </Table.Row>
          <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
            <Table.Data>
              <FlexBox alignItems="center" gap={space(1)}>
                <ArrowIcon
                  color={theme.colorPalette.grey400}
                  fontSize={theme.font.size.default.base}
                />
                <Typography color="black" size="xs" weight="semiBold">
                  {t("report.status")}
                </Typography>
              </FlexBox>
            </Table.Data>
            <Table.Data>
              <FlexBox
                alignItems="flex-start"
                flexDirection="column"
                gap={space(1)}
              >
                <Typography size="sm">
                  {t(`incident.status.${incident.status}`)}
                </Typography>
                {incident.active === true && (
                  <FlexBox alignItems="center" gap={space(1)}>
                    <TimeIcon
                      color={theme.colorPalette.grey500}
                      fontSize={theme.font.size.default.xs}
                    />
                    <Typography color="grey500" italic size="xs">
                      {t("report.status.waitingForResolution")}
                    </Typography>
                  </FlexBox>
                )}
              </FlexBox>
            </Table.Data>
          </Table.Row>
        </Table.Body>
      </Table>
      {(() => {
        const verlaufEntries = history;
        if (verlaufEntries.length === 0) return null;
        return (
          <>
            <Typography color="grey500" size="xs" weight="semiBold">
              {t("report.history")}
            </Typography>
            <StyledTimeline>
              <StyledTimelineInner>
                {verlaufEntries.map((entry, idx) => {
                  const showMessage =
                    entry.message &&
                    !REDUNDANT_AUDIT_MESSAGES.has(
                      entry.message.trim().toLowerCase()
                    );
                  return (
                    <StyledTimelineEntry
                      dotColor={dotColorFor(entry)}
                      key={`${entry.updatedAt}-${entry.status}-${
                        entry.message ?? ""
                      }`}
                    >
                      <FlexBox
                        alignItems="center"
                        gap={space(2)}
                        marginBottom={showMessage ? space(1) : 0}
                      >
                        <Tag minor variant={tagVariantFor(entry)}>
                          {statusLabelOf(entry, t)}
                        </Tag>
                        <Typography color="grey500" size="xs">
                          {formatDateWithSuffix(new Date(entry.updatedAt))}
                        </Typography>
                        {/* Dauer der Eskalation (nur Warning/Error) — Uhr + h/m,
                        rechtsbündig am Zeilenende. */}
                        {entry.status === "NEW" && entrySeverityTag(entry) && (
                          <FlexBox
                            alignItems="center"
                            gap={space(1)}
                            marginLeft="auto"
                          >
                            <TimeIcon
                              color={theme.colorPalette.grey400}
                              fontSize={12}
                            />
                            <Typography color="grey500" size="xs">
                              {escalationDuration(verlaufEntries, idx)}
                            </Typography>
                          </FlexBox>
                        )}
                        {entry.origin === "user" && entry.originId && (
                          <Typography color="grey600" size="xs">
                            · {entry.originId}
                          </Typography>
                        )}
                      </FlexBox>
                      {showMessage && (
                        <Typography color="black" size="sm">
                          {/* Roh aus dem OPC-UA-Event (Englisch), keine Übersetzung/Umformung. */}
                          {entry.message}
                        </Typography>
                      )}
                    </StyledTimelineEntry>
                  );
                })}
              </StyledTimelineInner>
            </StyledTimeline>
          </>
        );
      })()}
      {/*
        Kommentar-Eingabe für den Bestätigen-Pfad. MUSS exakt an dieselbe
        Bedingung wie der „Bestätigen"-Button gekoppelt sein (error-report-
        modal.tsx): aufgelöst (active === false) UND bereits quittiert
        (acknowledged) UND noch nicht bestätigt. Sonst erscheint das Feld
        ohne zugehörigen Button — und flackert bei einem chatternden Alarm
        im active↔resolved-Takt ein/aus, obwohl gar nicht quittiert wurde.
        Der Ack- und der Report-Pfad bringen ihre eigenen Kommentar-Felder
        mit (Modal-Step 2 / ErrorReportTwo).
      */}
      {incident.strictAudited !== false &&
        incident.active === false &&
        incident.acknowledged &&
        !incident.confirmed && (
          <FlexBox flexDirection="column" gap={space(1)} marginTop={space(4)}>
            <FlexBox alignItems="baseline" gap={space(1)}>
              <Typography color="black" size="sm" weight="semiBold">
                {t("report.commentlabel")}
              </Typography>
              {!comment.trim() && (
                <span
                  style={{
                    color: theme.color.error,
                    fontSize: theme.font.size.default.xs,
                  }}
                >
                  * {t("report.required")}
                </span>
              )}
            </FlexBox>
            <StyledTextarea
              $invalid={!comment.trim()}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("report.commentplaceholder")}
              value={comment}
            />
          </FlexBox>
        )}
    </>
  );
};
